import http from '../../../api/axios';
import { apiUrl } from '../../../api/apiUrl';
import { Values } from '../../../forms/CodeBookItems/CodeBookItems.types';
import { handleApiErrors } from '../../../api/apiErrorHandler';
import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { CodeBookItemsForm } from '../../../forms/CodeBookItems/CodeBookItemsForm';
import { CodeBookItem } from '../../../types/CodeBookItem';
import { Loader } from '../../../components/loader/Loader';
import { useTranslation } from '../../../context/translationContext';
import { CodeBookItemsPricePage } from '../CodeBookItemPrice/CodeBookItemPricePage';
import { paths } from '../../RouterContainer';
import { useBreadcrumbs } from '../../../hooks/useBreadcrumbs';
import { useLoading } from '../../../context/loadingContext';
import { useCurrentCompany } from '../../../hooks/useCompany';

/**
 * Page component for editing existing code book items of company
 */
export const CodeBookItemsUpdatePage = () => {
  const { id: codeBookItemId } = useParams();
  const { company } = useCurrentCompany();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [data, setData] = useState<CodeBookItem | null>(null);
  const { startLoading, stopLoading } = useLoading();
  const { setBreadcrumbs } = useBreadcrumbs();
  useEffect(() => {
    setBreadcrumbs([
      { path: paths.CODE_BOOK_ITEMS, name: 'navigation.code-book-items' },
      {
        path: `${paths.CODE_BOOK_ITEMS}/edit/${codeBookItemId}`,
        name: 'navigation.code-book-item-edit',
      },
    ]);
  }, []);

  /**
   * Loads company code book item
   */
  const fetchData = async () => {
    const response = await http.apiGet({
      url: `${apiUrl.CODE_BOOK_ITEM_ITEM}?id=${codeBookItemId}`,
    });
    setData(response?.data);
  };

  useEffect(() => {
    fetchData();
  }, []);

  /**
   * Handles edit action for code book item form submit.
   * @param values form data
   */
  const handleSubmit = async (values: Values) => {
    try {
      startLoading();
      const response = await http.apiPut({ url: apiUrl.CODE_BOOK_ITEM_UPDATE, data: values });
      if (response?.status === 200) {
        stopLoading();
        toast.success('Success!');
      }
    } catch (err) {
      stopLoading();
      handleApiErrors(err);
    }
  };

  /**
   * If company changes while on codeBookItem detail redirect to codeBookItem overview
   */
  useEffect(() => {
    if (!data || !company || company?.id === data?.companyId) return;
    navigate(`/${paths.CODE_BOOK_ITEMS}`);
  }, [company, data]);

  if (data === null) return <Loader />;
  return (
    <>
      <h1 className="text-3xl font-bold">{t('page.code-book-item.headline-edit')}</h1>
      <div className="flex">
        <div className="w-2/5 mr-5">
          <CodeBookItemsForm onSubmit={handleSubmit} initialValues={data as Values} />
        </div>
        <div className="w-full">
          <CodeBookItemsPricePage />
        </div>
      </div>
    </>
  );
};
